/**
 * WebSdkDocs — Public documentation for the Cyannect Web SDK.
 * Loop MWEB-2 · Route: /docs/web-sdk (cyannect.com host).
 *
 * Documents the EXISTING SDK at /sdk/v1/cyannect.js and the EXISTING
 * ingest endpoint POST /api/v1/track/sdk. Does not invent a new API.
 *
 * Rule N: this page must never imply a mobile app is required. A working
 * merchant website + this SDK is enough for full activation.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Copy, Check, Rocket, MousePointerClick, ShieldCheck, Zap, BookOpen,
  ExternalLink, Info,
} from "lucide-react";

function CopyBlock({ code, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative group">
      <pre className="bg-slate-900 text-slate-100 text-xs md:text-sm p-4 md:p-5 rounded-2xl overflow-x-auto leading-relaxed"
           data-testid={testid}>
        <code>{code}</code>
      </pre>
      <button
        onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1600); }}
        className="absolute top-3 right-3 inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700"
        data-testid={`${testid}-copy`}
        aria-label="Copy snippet"
      >
        {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-4">
      <div className="w-9 h-9 rounded-full bg-cyan-500 text-white flex items-center justify-center font-semibold text-sm shrink-0">
        {n}
      </div>
      <div>
        <div className="font-display text-lg text-slate-900">{title}</div>
        <div className="text-sm text-slate-600 mt-2 leading-relaxed space-y-3">{children}</div>
      </div>
    </div>
  );
}

export default function WebSdkDocs() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://cyannect.com";

  // Step 1 — one-line inclusion. Matches actual SDK behaviour.
  const includeSnippet = `<script src="${origin}/sdk/v1/cyannect.js"></script>`;

  // Step 2 — init snippet. The `merchantId` is the merchant's Cyannect
  // store id (public, safe to expose in browser — mirrors what /api/v1/track/sdk
  // already validates server-side).
  const initSnippet =
`<!-- Place just before </body> or in the site header, once per page -->
<script src="${origin}/sdk/v1/cyannect.js"></script>
<script>
  Cyannect.init({
    merchantId: "YOUR_STORE_ID"        // find it in Merchant → Integrations
  });
</script>`;

  // Step 3 — funnel event examples (non-financial). Real events supported by
  // POST /api/v1/track/sdk today — see routers/iter39.py.
  const trackSnippet =
`// Product page
Cyannect.productViewed({ sku: "RUN-SHOE-123", price: 129.00 });

// Cart
Cyannect.addToCart({ sku: "RUN-SHOE-123", qty: 1 });

// Checkout entry (browser event — NOT the source of truth for money)
Cyannect.checkoutStarted({ cart_value: 129.00, currency: "CAD" });`;

  // Step 4 — verified conversion is server-to-server. Never in the browser.
  const conversionSnippet =
`# On order-paid webhook, from YOUR backend (never the browser):
curl -X POST "${origin}/api/v1/conversions" \\
  -H "Authorization: Bearer YOUR_MERCHANT_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "cy_ref":     "CY-XXXXXXXX",
    "order_id":   "1042",
    "gross":      129.00,
    "currency":   "CAD"
  }'`;

  const events = useMemo(() => ([
    { code: "PRODUCT_VIEWED",  when: "Product detail page loads." },
    { code: "ADD_TO_CART",     when: "Shopper adds a SKU to their cart." },
    { code: "REMOVE_FROM_CART", when: "Shopper removes a SKU from their cart." },
    { code: "CHECKOUT_STARTED", when: "Shopper enters the checkout flow." },
    { code: "OFFER_VALIDATED", when: "Cyannect confirms a code is redeemable (optional)." },
    { code: "PURCHASE_INTENT", when: "Any strong-intent signal before a verified conversion." },
  ]), []);

  return (
    <div className="max-w-4xl mx-auto px-6 py-14" data-testid="web-sdk-docs">
      {/* Hero */}
      <div className="mb-10">
        <div className="inline-flex items-center gap-1.5 pf-chip pf-chip-cyan text-[10px] font-semibold">
          <BookOpen className="w-3 h-3" /> Developer docs
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight text-slate-900 mt-4">
          Cyannect Web SDK
        </h1>
        <p className="text-lg text-slate-600 mt-3 max-w-2xl leading-relaxed">
          Add Cyannect referral and funnel tracking to your website in minutes.
          A single script keeps referral attribution intact and reports
          approved non-financial funnel events — all without shipping a
          mobile app.
        </p>

        <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
             data-testid="web-sdk-docs-website-first">
          <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-900 leading-relaxed">
            <b>A merchant mobile app is not required.</b> Website-only merchants are fully
            supported on Cyannect. If your checkout runs on your website, this SDK plus
            server-to-server conversion reporting is all you need to activate.
          </div>
        </div>
      </div>

      {/* Tracking levels */}
      <section className="mb-12" data-testid="web-sdk-docs-tracking-levels">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
          What Cyannect tracks
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="pf-card p-5">
            <MousePointerClick className="w-5 h-5 text-cyan-700" />
            <div className="font-display text-lg text-slate-900 mt-2">Referral</div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Offer clicks, referral creation, and outbound redirects. Handled by Cyannect —
              zero merchant integration.
            </p>
          </div>
          <div className="pf-card p-5">
            <Zap className="w-5 h-5 text-amber-600" />
            <div className="font-display text-lg text-slate-900 mt-2">Website funnel</div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Landing, product-view, add-to-cart and checkout-start events fired by this
              SDK. Advisory analytics only — never used to compute money.
            </p>
          </div>
          <div className="pf-card p-5">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
            <div className="font-display text-lg text-slate-900 mt-2">Verified conversion</div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Confirmed via <code className="font-mono text-[11px] bg-slate-100 px-1 rounded">
              POST /api/v1/conversions</code> from your server with a bearer key. Browser
              events cannot create financial truth.
            </p>
          </div>
        </div>
      </section>

      {/* Installation */}
      <section className="mb-14 space-y-10" data-testid="web-sdk-docs-install">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
          <Rocket className="w-3 h-3" /> Install in 4 steps
        </div>

        <Step n="1" title="Copy the Cyannect script tag">
          <p>Load <code className="font-mono text-xs bg-slate-100 px-1 rounded">cyannect.js</code> from our CDN. Zero dependencies, ~4 KB gzipped.</p>
          <CopyBlock code={includeSnippet} testid="web-sdk-step-1" />
        </Step>

        <Step n="2" title="Add it before your closing body tag">
          <p>Include the script once per page — the site header or the very bottom of
          <code className="font-mono text-xs bg-slate-100 px-1 rounded">&lt;body&gt;</code>
          both work. Then initialize with your <b>merchant / store ID</b> (find it under
          <Link to="/merchant/integrations" className="text-cyan-700 hover:underline"> Merchant → Integrations</Link>).</p>
          <CopyBlock code={initSnippet} testid="web-sdk-step-2" />
          <div className="text-xs text-slate-500 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            The <code className="font-mono">merchantId</code> is public and safe to expose in the
            browser — Cyannect validates every event against your storefront on the server.
            Never place API secrets or bearer keys in browser JavaScript.
          </div>
        </Step>

        <Step n="3" title="Report non-financial funnel events (optional)">
          <p>Fire browser-safe events from your site pages. These are <b>advisory</b> — Cyannect uses them for landing/funnel analytics but never for payouts.</p>
          <CopyBlock code={trackSnippet} testid="web-sdk-step-3" />
          <div className="text-xs text-slate-500">
            Supported events today:{" "}
            {events.map((e, i) => (
              <span key={e.code}>
                <code className="font-mono text-[11px] bg-slate-100 px-1 rounded">{e.code}</code>
                {i < events.length - 1 ? ", " : ""}
              </span>
            ))}
          </div>
        </Step>

        <Step n="4" title="Report verified conversions from your server">
          <p>
            When an order is paid, POST it from your backend with your merchant bearer
            key. This is the <b>only</b> path that drives cashback and performance fees.
          </p>
          <CopyBlock code={conversionSnippet} testid="web-sdk-step-4" />
          <div className="text-xs text-slate-500">
            Grab (or rotate) your key at{" "}
            <Link to="/merchant/developer" className="text-cyan-700 hover:underline">
              Merchant → Developer
            </Link>.
          </div>
        </Step>
      </section>

      {/* Event reference */}
      <section className="mb-14" data-testid="web-sdk-docs-event-reference">
        <h2 className="font-display text-2xl text-slate-900">Event reference</h2>
        <p className="text-sm text-slate-600 mt-1 mb-4">Non-financial funnel events consumed by
          <code className="font-mono text-[11px] bg-slate-100 px-1 rounded"> POST /api/v1/track/sdk</code>.
        </p>
        <div className="pf-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">When to fire</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.code} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-mono text-xs text-slate-800">{e.code}</td>
                  <td className="px-4 py-3 text-slate-700">{e.when}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Security */}
      <section className="mb-16" data-testid="web-sdk-docs-security">
        <h2 className="font-display text-2xl text-slate-900">Security notes</h2>
        <ul className="mt-3 text-sm text-slate-700 space-y-2 list-disc list-inside leading-relaxed">
          <li>The SDK <b>never</b> handles secrets. Only your public <code className="font-mono text-xs bg-slate-100 px-1 rounded">merchantId</code> lives in the browser.</li>
          <li>Verified conversions require a merchant API key over HTTPS from your server — never from the browser.</li>
          <li><code className="font-mono text-xs bg-slate-100 px-1 rounded">cy_ref</code> is stored as a first-party cookie for 30 days and stripped from the URL on load.</li>
          <li>Cross-merchant events are dropped server-side; a bad payload never breaks your checkout.</li>
        </ul>
      </section>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-700">Need to test live payloads?</div>
        <Link
          to="/developers"
          className="text-sm font-semibold text-cyan-700 hover:text-cyan-800 inline-flex items-center gap-1"
          data-testid="web-sdk-docs-playground-link"
        >
          Open the SDK playground <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
